import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { isChunkLoadError, mayReloadForNewBuild, reloadForNewBuild } from '@/lib/staleBuild';

type Props = WithTranslation & {
  children: ReactNode;
  /** Rendered instead of the default panel. Receives nothing — keep it self-contained. */
  fallback?: ReactNode;
  /** Hook for a logger. Never send the message to a third party from here. */
  onError?: (error: Error, info: ErrorInfo) => void;
};

type State = { error: Error | null; reloading: boolean };

/**
 * The last line of defence. A crashed screen must never leave a trader staring at a blank
 * page while bots are running, so the fallback says what happened and offers a reload.
 *
 * A page chunk that no longer exists means the tab predates a deploy; that one reloads itself
 * (lib/staleBuild.ts) instead of showing the panel.
 *
 * The raw error message is shown in mono — this is a self-hosted product and the person
 * looking at it is the operator.
 */
class ErrorBoundaryBase extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, reloading: isChunkLoadError(error) && mayReloadForNewBuild() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    if (this.state.reloading && !reloadForNewBuild()) this.setState({ reloading: false });
  }

  private reset = (): void => {
    this.setState({ error: null, reloading: false });
  };

  private reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error, reloading } = this.state;
    const { children, fallback, t } = this.props;

    if (!error) return children;
    if (reloading) return null;
    if (fallback) return fallback;

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card elevation={1} padding="lg" className="w-full max-w-form">
          <div className="flex items-start gap-3">
            <TriangleAlert
              aria-hidden="true"
              strokeWidth={1.5}
              className="mt-0.5 h-5 w-5 shrink-0 text-loss"
            />
            <div className="min-w-0">
              <h2 className="text-heading text-ink">{t('errors.boundaryTitle')}</h2>
              <p className="mt-1.5 text-body-md text-ink-secondary">
                {t('errors.boundaryDescription')}
              </p>
              <pre className="mt-3 max-h-32 overflow-auto whitespace-pre-wrap rounded-sm border border-border-subtle bg-surface-2 p-2.5 font-mono text-num-sm text-ink-tertiary">
                {error.message}
              </pre>
              <div className="mt-4 flex gap-2">
                <Button variant="primary" onClick={this.reload} iconLeft={<RotateCcw strokeWidth={1.5} />}>
                  {t('errors.reload')}
                </Button>
                <Button variant="secondary" onClick={this.reset}>
                  {t('common.retry')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryBase);
